import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, CheckCircle2, MessageCircle } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { API, CITIES, IMAGES, WHATSAPP_COMMUNITY_URL } from "../data/site";

const BOX_OPTIONS = {
  corporate: ["25–50 boxes", "50–100 boxes", "100+ boxes"],
  family: ["2–5 boxes", "6–10 boxes", "10+ boxes"],
};

export const GiftBoxSection = () => {
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", city: "delhi_ncr", gifting: "family", boxes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/giftbox`, { ...form, boxes: form.boxes || null });
      setDone(true);
      toast.success("Gift box interest registered!");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-paper-card border border-[#D6CEBF] rounded-xl px-4 py-3 text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-turmeric/70 text-sm";

  const toggleCls = (active) =>
    `flex-1 rounded-full px-4 py-2.5 text-sm font-semibold border transition-all duration-300 ${
      active ? "bg-turmeric text-ink border-turmeric" : "border-[#D6CEBF] text-ink-soft hover:border-ink bg-paper-card"
    }`;

  return (
    <section className="py-16 md:py-24 bg-turmeric-light/40 border-b border-[#E7E2D7]" data-testid="giftbox-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-4 flex items-center gap-2">
            <Gift size={14} /> Diwali Gifting — 1 November 2026
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink font-semibold leading-tight mb-5">
            Gift boxes people actually finish.
          </h2>
          <p className="text-base leading-relaxed text-ink-soft max-w-lg mb-8">
            The Diwali Drop includes gifting boxes: besan-date laddus, millet kheer cups, roasted
            makhana and badam mylk — no refined sugar, no refined-oil floods, packaging worth keeping.
            Register interest for corporate bulk orders or family gifting and we will reach out with
            the catalogue first.
          </p>
          <div className="overflow-hidden rounded-3xl shadow-lg max-w-md">
            <img loading="lazy" decoding="async"
              src={IMAGES.saladGrid}
              alt="Assorted fresh festive bowls arranged for gifting"
              className="w-full h-56 object-cover transition-transform duration-700 hover:scale-105"
            />
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-6">
          <div className="bg-paper-card border border-[#E7E2D7] rounded-3xl p-7 md:p-9 shadow-lg">
            {done ? (
              <div data-testid="giftbox-success" className="text-center py-6">
                <CheckCircle2 className="mx-auto mb-3 text-leaf" size={36} />
                <h3 className="font-serif text-2xl font-semibold text-ink mb-2">You are on the gifting list.</h3>
                <p className="text-sm text-ink-soft mb-6">
                  We will send the Diwali gift box catalogue to <span className="font-semibold">{form.email}</span> before
                  anyone else sees it.
                </p>
                <a
                  href={WHATSAPP_COMMUNITY_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-leaf text-white font-semibold text-sm px-6 py-3 transition-all hover:-translate-y-0.5 hover:shadow-lg"
                >
                  <MessageCircle size={16} /> Join the WhatsApp Community
                </a>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="giftbox-form" className="space-y-4">
                <div className="flex gap-2" role="group" aria-label="Gifting type">
                  <button type="button" data-testid="giftbox-type-family" onClick={() => setForm({ ...form, gifting: "family", boxes: "" })} className={toggleCls(form.gifting === "family")}>
                    Family Gifting
                  </button>
                  <button type="button" data-testid="giftbox-type-corporate" onClick={() => setForm({ ...form, gifting: "corporate", boxes: "" })} className={toggleCls(form.gifting === "corporate")}>
                    Corporate Bulk
                  </button>
                </div>
                <div className="flex gap-2" role="group" aria-label="City">
                  {CITIES.map((c) => (
                    <button key={c.id} type="button" data-testid={`giftbox-city-${c.id === "delhi_ncr" ? "delhi" : "bengaluru"}`} onClick={() => setForm({ ...form, city: c.id })} className={toggleCls(form.city === c.id)}>
                      {c.label}
                    </button>
                  ))}
                </div>
                <input data-testid="giftbox-name-input" className={inputCls} placeholder={form.gifting === "corporate" ? "Your name & company" : "Your name"} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input data-testid="giftbox-email-input" className={inputCls} type="email" required placeholder="Email address *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input data-testid="giftbox-whatsapp-input" className={inputCls} placeholder="WhatsApp number (optional)" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
                <select data-testid="giftbox-boxes-select" className={`${inputCls} appearance-none`} value={form.boxes} onChange={(e) => setForm({ ...form, boxes: e.target.value })}>
                  <option value="">How many boxes, roughly? (optional)</option>
                  {BOX_OPTIONS[form.gifting].map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
                <button
                  data-testid="giftbox-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-beet text-white font-bold text-sm px-6 py-4 tracking-wide transition-all duration-300 hover:bg-beet-deep hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60"
                >
                  {loading ? "Registering..." : "Register Gift Box Interest"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
